import { useReaderAppreciation, AppreciationPrice, AppreciationInvitation } from "./ReaderAppreciation";
import { readerPrice } from "../../utils/readerAppreciation";
import { displayLabel } from "../../utils/displayLabel";
import React, { useEffect, useMemo, useState } from "react";
import CheckoutModal from "./CheckoutModal";
import { Minus, Plus, ShoppingCart, Trash2 } from "lucide-react";
import { useNavigate } from "react-router-dom";
import {
  getAuthorCart,
  removeFromAuthorCart,
  updateAuthorCartQuantity,
} from "./AuthorCart";

const PLACEHOLDER = "https://placehold.co/120x180?text=No+Image";

export default function AuthorCartPage() {
  const appreciation = useReaderAppreciation();
  const effectivePrice = item => readerPrice(item?.price || 0, item?.format, appreciation.participating, appreciation.member) || 0;
  const navigate = useNavigate();
  const [cartItems, setCartItems] = useState([]);
  const [isCheckoutOpen, setIsCheckoutOpen] = useState(false);

  const loadCart = () => {
    setCartItems(getAuthorCart());
  };

  useEffect(() => {
    loadCart();

    const handleCartUpdate = () => {
      loadCart();
    };

    window.addEventListener("bookish-cart-updated", handleCartUpdate);
    window.addEventListener("storage", handleCartUpdate);

    return () => {
      window.removeEventListener("bookish-cart-updated", handleCartUpdate);
      window.removeEventListener("storage", handleCartUpdate);
    };
  }, []);

  const subtotal = useMemo(() => {
    return cartItems.reduce((total, item) => {
      return (
        total +
        (readerPrice(item?.price || 0, item?.format, appreciation.participating, appreciation.member) || 0) * Number(item?.quantity || 1)
      );
    }, 0);
  }, [cartItems, appreciation.participating, appreciation.member]);

  const totalQuantity = useMemo(() => {
    return cartItems.reduce(
      (total, item) => total + Number(item?.quantity || 1),
      0
    );
  }, [cartItems]);

  const handleQuantityChange = (item, nextQuantity) => {
    const stockQuantity = Number(item?.stockQuantity ?? item?.availableQuantity);

    if (nextQuantity < 1) return;

    if (
      Number.isFinite(stockQuantity) &&
      stockQuantity >= 0 &&
      nextQuantity > stockQuantity
    ) {
      return;
    }

    updateAuthorCartQuantity(item.cartItemId, nextQuantity);
    loadCart();
  };

  const handleRemove = (cartItemId) => {
    removeFromAuthorCart(cartItemId);
    loadCart();
  };

  const handleContinueShopping = () => {
    navigate("/bookstore");
  };

  if (cartItems.length === 0) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center px-4 py-12">
        <div className="w-full max-w-xl rounded-[28px] border border-bookish-purple/15 bg-white p-8 text-center shadow-sm">
          <ShoppingCart className="mx-auto mb-4 h-12 w-12 text-bookish-purple/50" />

          <h1 className="text-3xl font-serif font-bold text-bookish-purple">
            Your Cart is Empty
          </h1>

          <p className="mt-3 text-bookish-purple/70">
            Browse this author's books and add something to your cart.
          </p>

          <button
            type="button"
            onClick={handleContinueShopping}
            className="mt-6 rounded-full bg-bookish-royal px-6 py-3 font-semibold text-white transition hover:bg-bookish-purple"
          >
            Browse Books
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="author-cart-page mx-auto w-full max-w-5xl px-2 py-6 md:px-4">
      {!appreciation.member && <AppreciationInvitation savings={cartItems.reduce((sum, item) => sum + (Number(item.price) - readerPrice(item.price, item.format, appreciation.participating, true)) * Number(item.quantity || 1), 0)} />}
      <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
        <div>
          <h1 className="text-3xl md:text-4xl font-serif font-bold text-bookish-purple">
            Your Cart
          </h1>

          <p className="mt-1 text-sm text-bookish-purple/65">
            {totalQuantity} {totalQuantity === 1 ? "item" : "items"}
          </p>
        </div>

        <button
          type="button"
          onClick={handleContinueShopping}
          className="rounded-full border border-bookish-purple/30 bg-white px-5 py-2 text-sm font-semibold text-bookish-purple transition hover:bg-bookish-purple/5"
        >
          Continue Shopping
        </button>
      </div>

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-[minmax(0,1fr)_320px]">
        <div className="space-y-4">
          {cartItems.map((item) => {
            const quantity = Number(item?.quantity || 1);
            const price = effectivePrice(item);
            const lineTotal = price * quantity;
            const stock = Number(item.stockQuantity ?? item.availableQuantity);

            return (
              <div
                key={item.cartItemId}
                className="rounded-2xl border border-bookish-purple/15 bg-white p-4 shadow-sm"
              >
                <div className="flex gap-4">
                  <img
                    src={item.coverImageUrl || PLACEHOLDER}
                    alt={displayLabel(item.title || "Book")}
                    className="h-36 w-24 flex-none rounded-md object-contain bg-bookish-cream/30"
                    onError={(e) => {
                      e.currentTarget.src = PLACEHOLDER;
                    }}
                  />

                  <div className="min-w-0 flex-1">
                    <div className="flex flex-col justify-between gap-2 sm:flex-row">
                      <div>
                        <h2 className="font-serif text-xl font-bold text-bookish-purple">
                          {displayLabel(item.title || "Book")}
                        </h2>

                        {item.authorName && (
                          <p className="mt-1 text-sm text-bookish-purple/65">
                            {displayLabel(item.authorName)}
                          </p>
                        )}

                        {item.format && (
                          <p className="mt-2 text-sm font-semibold text-bookish-purple">
                            {displayLabel(item.format)}
                          </p>
                        )}

                        {item.variationName && (
                          <p className="mt-1 text-sm text-bookish-purpleAccent">
                            {displayLabel(item.variationName)}
                          </p>
                        )}

                        {item?.shipping?.freeShipping && (
                          <p className="mt-2 text-xs font-semibold text-bookish-purpleAccent">
                            Free Shipping
                          </p>
                        )}
                      </div>

                      <div className="sm:text-right">
                        <p className="font-semibold text-bookish-purple">
                          <AppreciationPrice price={item.price} format={item.format} />
                        </p>

                        {quantity > 1 && (
                          <p className="mt-1 text-xs text-bookish-purple/55">
                            ${lineTotal.toFixed(2)} total
                          </p>
                        )}
                      </div>
                    </div>

                    <div className="mt-5 flex flex-wrap items-center justify-between gap-4">
                      <div className="inline-flex items-center rounded-full border border-bookish-purple/20 bg-bookish-cream/20">
                        <button
                          type="button"
                          onClick={() =>
                            handleQuantityChange(item, quantity - 1)
                          }
                          disabled={quantity <= 1}
                          className="flex h-11 w-11 items-center justify-center rounded-l-full text-bookish-purple transition hover:bg-bookish-purple/10 disabled:cursor-not-allowed disabled:opacity-35"
                          aria-label={`Decrease quantity of ${displayLabel(item.title)}`}
                        >
                          <Minus className="h-4 w-4" />
                        </button>

                        <span className="min-w-[42px] text-center text-sm font-semibold text-bookish-purple">
                          {quantity}
                        </span>

                        <button
                          type="button"
                          onClick={() =>
                            handleQuantityChange(item, quantity + 1)
                          }
                          className="flex h-11 w-11 items-center justify-center rounded-r-full text-bookish-purple transition hover:bg-bookish-purple/10"
                          disabled={Number.isFinite(stock) && stock >= 0 && quantity >= stock}
                          aria-label={`Increase quantity of ${displayLabel(item.title)}`}
                        >
                          <Plus className="h-4 w-4" />
                        </button>
                      </div>

                      <button
                        type="button"
                        onClick={() => handleRemove(item.cartItemId)}
                        className="inline-flex min-h-[44px] items-center gap-1.5 text-sm font-semibold text-red-700 transition hover:text-red-900"
                      >
                        <Trash2 className="h-4 w-4" />
                        Remove
                      </button>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>

        <div className="h-fit rounded-2xl border border-bookish-purple/15 bg-white p-5 shadow-sm lg:sticky lg:top-20">
          <h2 className="text-xl font-serif font-bold text-bookish-purple">
            Order Summary
          </h2>

          <div className="mt-5 flex justify-between text-sm text-bookish-purple/75">
            <span>
              Subtotal ({totalQuantity}{" "}
              {totalQuantity === 1 ? "item" : "items"})
            </span>

            <span className="font-semibold text-bookish-purple">
              ${subtotal.toFixed(2)}
            </span>
          </div>

          <div className="mt-3 flex justify-between text-sm text-bookish-purple/60">
            <span>Shipping</span>
            <span>Calculated at checkout</span>
          </div>

          <div className="mt-3 flex justify-between text-sm text-bookish-purple/60">
            <span>Tax</span>
            <span>Calculated at checkout</span>
          </div>

          <div className="my-5 border-t border-bookish-purple/15" />

          <div className="flex items-center justify-between">
            <span className="font-semibold text-bookish-purple">
              Subtotal
            </span>

            <span className="text-xl font-bold text-bookish-purple">
              ${subtotal.toFixed(2)}
            </span>
          </div>

          <button
            type="button"
            onClick={() => setIsCheckoutOpen(true)}
            className="mt-6 w-full rounded-md bg-bookish-royal px-5 py-3 font-semibold text-white transition hover:bg-bookish-purple"
          >
            Checkout
          </button>

          <p className="mt-3 text-center text-xs leading-5 text-bookish-purple/50">
            Shipping and taxes will be calculated before payment.
          </p>
        </div>
        <CheckoutModal
        isOpen={isCheckoutOpen}
        onClose={() => setIsCheckoutOpen(false)}
        cartItems={cartItems}
        />
      </div>
    </div>
  );
}
